import { defineStore } from 'pinia'
import {
  isBiometricSupported,
  registerBiometricCredential,
  verifyBiometricUnlock,
} from '@/globals/typescript/utils/biometricAuth'
import { hashPin, verifyPin } from '@/globals/typescript/utils/pinAuth'

// MIL-127 — app-lock variant only (Face ID/fingerprint/WebAuthn to unlock an
// already-authenticated session after backgrounding, NOT biometric-gated
// login). `isEnabled`/`credentialID`/`pinHash` are a per-device preference
// (like the `lang` locale), so they persist across logins on the same device
// rather than being tied to a specific Firebase user — this is a UX
// affordance, not a security boundary enforced server-side.
//
// Two mutually exclusive unlock methods: `credentialID` (biometric, via
// biometricAuth.ts) or `pinHash` (PIN, via pinAuth.ts — the fallback for
// browsers where the platform authenticator isn't available). Enabling one
// clears the other.
const APP_LOCK_ENABLED_KEY = 'appLock:isEnabled'
const APP_LOCK_CREDENTIAL_KEY = 'appLock:credentialID'
const APP_LOCK_PIN_HASH_KEY = 'appLock:pinHash'

export const useAppLockStore = defineStore('appLock', {
  state: () => {
    const isEnabled = localStorage.getItem(APP_LOCK_ENABLED_KEY) === 'true'
    return {
      isEnabled,
      credentialID: localStorage.getItem(APP_LOCK_CREDENTIAL_KEY),
      pinHash: localStorage.getItem(APP_LOCK_PIN_HASH_KEY),
      // Start locked on every cold start when app-lock is enabled — otherwise
      // a fully closed-and-reopened app (not just backgrounded) never asks
      // for Face ID, since `lock()` is only ever called from the
      // background-detection listener in App.vue.
      isLocked: isEnabled,
      isSupported: null as boolean | null,
    }
  },

  getters: {
    unlockMethod(state): 'biometric' | 'pin' | null {
      if (state.credentialID) return 'biometric'
      if (state.pinHash) return 'pin'
      return null
    },
  },

  actions: {
    async checkSupport(): Promise<boolean> {
      this.isSupported = await isBiometricSupported()
      return this.isSupported
    },

    async enable(userID: string): Promise<void> {
      const credentialID = await registerBiometricCredential(userID)

      this.credentialID = credentialID
      this.pinHash = null
      this.isEnabled = true
      localStorage.setItem(APP_LOCK_CREDENTIAL_KEY, credentialID)
      localStorage.removeItem(APP_LOCK_PIN_HASH_KEY)
      localStorage.setItem(APP_LOCK_ENABLED_KEY, 'true')
    },

    async enablePin(pin: string): Promise<void> {
      const pinHash = await hashPin(pin)

      this.pinHash = pinHash
      this.credentialID = null
      this.isEnabled = true
      localStorage.setItem(APP_LOCK_PIN_HASH_KEY, pinHash)
      localStorage.removeItem(APP_LOCK_CREDENTIAL_KEY)
      localStorage.setItem(APP_LOCK_ENABLED_KEY, 'true')
    },

    disable(): void {
      this.isEnabled = false
      this.credentialID = null
      this.pinHash = null
      this.isLocked = false
      localStorage.removeItem(APP_LOCK_CREDENTIAL_KEY)
      localStorage.removeItem(APP_LOCK_PIN_HASH_KEY)
      localStorage.removeItem(APP_LOCK_ENABLED_KEY)
    },

    // Called when the app backgrounds (see App.vue's visibilitychange
    // listener). A no-op when app-lock isn't enabled.
    lock(): void {
      if (!this.isEnabled) return
      this.isLocked = true
    },

    // `pin` is only used when unlockMethod is 'pin' — ignored otherwise.
    async unlock(pin?: string): Promise<boolean> {
      let success = false

      if (this.credentialID) {
        success = await verifyBiometricUnlock(this.credentialID)
      } else if (this.pinHash && pin) {
        success = await verifyPin(pin, this.pinHash)
      } else {
        return false
      }

      if (success) this.isLocked = false

      return success
    },

    // Only clears the transient locked flag — `isEnabled`/`credentialID`/
    // `pinHash` stay, since they're a device preference, not session-scoped
    // auth state.
    resetStore(): void {
      this.isLocked = false
    },
  },
})
